import { MAX_TIMER_MS } from './timer';

/** The default for `BackoffConfig.initialMs`. */
const DEFAULT_INITIAL_MS = 1000;

/** The default for `BackoffConfig.maxMs`. It bounds the ceiling and nothing else. */
const DEFAULT_MAX_MS = 60_000;

/**
 * The exponential shape of a backoff: a first ceiling that doubles on every
 * attempt after it, up to a cap, with the actual wait drawn below the ceiling.
 *
 * @remarks
 * This is the only shape that COMPUTES a wait, and the only one that SPREADS
 * one. Naming a number or a list means you meant those numbers, and there is
 * nothing left to draw. Handing over this object means the library decides, and
 * deciding includes spreading. The shape you write already says which you
 * wanted, so there is no switch to set.
 *
 * The wait before attempt `n + 1` is drawn from `[0, min(maxMs, initialMs * 2 ** (n - 1))]`
 * (full jitter) and rounded UP to a whole millisecond, so a draw never collapses
 * to an immediate retry by rounding alone.
 *
 * A negative value, or one above 2_147_483_647 ms, is refused with a
 * `RangeError` when the policy is created.
 */
export interface BackoffConfig {
  /**
   * The first ceiling, doubling on every attempt after it.
   *
   * @remarks
   * NOT the wait itself: it is the CEILING of a draw over `[0, it]`, so a run
   * averages half of it. Wanting the wait itself is what a number or a list is
   * for. At four attempts `{ initialMs: 1000 }` computes the ceilings `[1000,
   * 2000, 4000]`. `0` is usable and means "try again at once".
   *
   * @defaultValue 1000, when the whole backoff is unset
   */
  initialMs: number;

  /**
   * The ceiling the doubling climbs towards. A value below `initialMs` is
   * allowed, and is then the ceiling from the first wait on.
   *
   * @defaultValue 60_000
   */
  maxMs?: number;
}

/**
 * How long to wait between attempts, in one of three shapes.
 *
 * @remarks
 * A NUMBER is that wait every time. A LIST is those waits in order, and needs
 * exactly `maxAttempts - 1` of them, because that is how many gaps there are.
 * An empty list means no wait at every gap. A `BackoffConfig` computes the
 * waits instead, and is the only shape that spreads them.
 *
 * Unset behaves as `{ initialMs: 1000, maxMs: 60_000 }`.
 */
export type Backoff = number | readonly number[] | BackoffConfig;

/**
 * A number in `[0, 1)`, from the platform generator.
 *
 * The generator is the platform's rather than `Math.random`, not because jitter
 * needs cryptographic strength, but because the lint gate has no exemptions and
 * the alternative is a suppression comment nobody revisits.
 *
 * There is no seam here on purpose. A choice of jitter shape has no measured
 * case behind it: full jitter was measured against the alternatives and won, so
 * an option would be a knob whose only settings are "the measured one" and
 * "worse".
 */
function randomFraction(): number {
  const draw = new Uint32Array(1);
  crypto.getRandomValues(draw);
  // A `Uint32Array` of length one always has an element. The fallback is what
  // `noUncheckedIndexedAccess` asks for and nothing reaches it.
  return (draw[0] ?? 0) / 2 ** 32;
}

/**
 * Computes the wait in milliseconds after a failed attempt.
 *
 * @remarks
 * A number is returned as it is, and a list element as it is, so neither is
 * doubled nor drawn under. An empty list, or a list shorter than the attempt,
 * yields 0. An object, or `undefined` with its defaults, draws with full jitter
 * under the doubled ceiling and rounds UP, because rounding down lets the draw
 * collapse to zero, the immediate retry the wait exists to prevent.
 *
 * The values are not validated here. `findBackoffProblems` does that once, when
 * the policy is created.
 *
 * @param backoff - the configured shape, or `undefined` for the defaults.
 * @param attempt - the 1-based attempt that just failed, so `1` is the wait
 *   before the second attempt.
 * @returns The wait in milliseconds. A fraction from a number or a list is kept.
 */
export function computeBackoffDelay(
  backoff: Backoff | undefined,
  attempt: number,
): number {
  if (typeof backoff === 'number') return backoff;

  // The list and the object are told apart on the one member the object must
  // carry. `Array.isArray` does not narrow a `readonly number[]` out of the
  // union on its false branch, so the object arm below would stop compiling.
  if (backoff !== undefined && !('initialMs' in backoff)) {
    // The fallback is also the whole of the empty-list rule: no wait named for
    // this gap is no wait.
    return backoff[attempt - 1] ?? 0;
  }

  // Unset or an object, in one branch on purpose: unset IS the object with its
  // defaults, so a second copy of the arithmetic would be the copy that drifts.
  const initialMs = backoff?.initialMs ?? DEFAULT_INITIAL_MS;
  // A zero first ceiling stays zero at every attempt. Without this the doubling
  // reaches `Infinity` at attempt 1025, and `0 * Infinity` is `NaN`.
  if (initialMs === 0) return 0;
  const ceiling = Math.min(
    backoff?.maxMs ?? DEFAULT_MAX_MS,
    initialMs * 2 ** (attempt - 1),
  );
  return Math.ceil(randomFraction() * ceiling);
}

/**
 * A whole number of something, at least one of it.
 *
 * A private copy: the client package that calls `findBackoffProblems` keeps
 * its own `usableCount` in its `limits.ts` for other fields, and the two should
 * stay equal.
 */
function usableCount(count: number): boolean {
  return Number.isInteger(count) && count >= 1;
}

/**
 * A wait the timer can perform for as long as it was asked to. Zero is usable
 * and means "try again at once", and a fraction is left alone because half a
 * millisecond is a timer that fires. The bounds carry `NaN` and the infinities
 * out on their own.
 *
 * A private copy: the client package that calls `findBackoffProblems` keeps
 * its own `usableDelay` in its `limits.ts` for other fields, and the two should
 * stay equal.
 */
function usableDelay(ms: number): boolean {
  return ms >= 0 && ms <= MAX_TIMER_MS;
}

/** One wording for every number that fails `usableDelay`, whatever its subject. */
function problemWithDelay(subject: string, ms: number): string {
  return `${subject} must be at least 0 and at most ${MAX_TIMER_MS} ms, not ${ms}`;
}

/**
 * Lists everything wrong with a backoff, whichever shape it has.
 *
 * @remarks
 * Every number in it is a delay, so `0` is accepted. A list element is named by
 * its position, because a position is the only handle a caller has on it.
 *
 * The length requirement is for a NON-EMPTY list only. A list names one wait per
 * gap, so `maxAttempts - 1` of them, and repeating the last value instead of
 * refusing would turn a typo into silent behavior. An empty list means a wait of
 * nought at every gap, so it is accepted at every `maxAttempts`.
 *
 * The length is judged only once `maxAttempts` itself is a whole number of at
 * least 1, so a caller who wrote `maxAttempts: NaN` is told about that by its
 * own check rather than about a number of gaps computed from it.
 *
 * @param backoff - the configured shape, or `undefined`, which has no problems.
 * @param maxAttempts - the total attempts including the first.
 * @param subject - how the messages name the backoff, such as `'backoff'`.
 * @returns One message per problem, empty when the backoff is usable.
 *
 * @example
 * ```ts
 * findBackoffProblems([10, -1], 3, 'backoff');
 * // ['backoff[1] must be at least 0 and at most 2147483647 ms, not -1']
 * ```
 */
export function findBackoffProblems(
  backoff: Backoff | undefined,
  maxAttempts: number,
  subject: string,
): string[] {
  if (backoff === undefined) return [];

  if (typeof backoff === 'number') {
    return usableDelay(backoff) ? [] : [problemWithDelay(subject, backoff)];
  }

  // Told apart on the one member the object must carry, for the reason
  // `computeBackoffDelay` gives.
  if (!('initialMs' in backoff)) {
    const problems: string[] = [];
    backoff.forEach((ms, index) => {
      if (!usableDelay(ms)) {
        problems.push(problemWithDelay(`${subject}[${index}]`, ms));
      }
    });

    const gaps = maxAttempts - 1;
    if (
      backoff.length > 0 &&
      usableCount(maxAttempts) &&
      backoff.length !== gaps
    ) {
      problems.push(
        `${subject} must name one wait per gap, so ${gaps} for ${maxAttempts} attempts, not ${backoff.length}`,
      );
    }
    return problems;
  }

  const problems: string[] = [];
  if (!usableDelay(backoff.initialMs)) {
    problems.push(problemWithDelay(`${subject}.initialMs`, backoff.initialMs));
  }
  if (backoff.maxMs !== undefined && !usableDelay(backoff.maxMs)) {
    problems.push(problemWithDelay(`${subject}.maxMs`, backoff.maxMs));
  }
  return problems;
}
